// Login.jsx
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Smartphone,
  ShieldCheck,
  CheckCircle
} from 'lucide-react';
import AuthLayout from '../helpers/AuthLayout';

const Login = () => {
  const [mode, setMode] = useState('email'); // email | phone
  const [showPassword, setShowPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    phone: '',
    otp: ''
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      if (value === '' || /^\d+$/.test(value)) {
        setFormData(prev => ({ ...prev, phone: value.slice(0, 10) }));
      }
    } else if (name === 'otp') {
      if (value === '' || /^\d+$/.test(value)) {
        setFormData(prev => ({ ...prev, otp: value.slice(0, 6) }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();

    if (mode === 'email') {
      console.log('Email Login:', {
        email: formData.email,
        password: formData.password
      });
    } else {
      console.log('Phone Login:', {
        phone: formData.phone,
        otp: formData.otp
      });
    }

    // ✅ Static success toast
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);

    // Reset form (static)
    setFormData({
      email: '',
      password: '',
      phone: '',
      otp: ''
    });
    setOtpSent(false);
  };

  const sendOtp = () => {
    if (formData.phone.length === 10) {
      setOtpSent(true);
      console.log('OTP sent to:', formData.phone);
    }
  };

  return (
    <AuthLayout>

      {/* ✅ SUCCESS TOAST */}
      <AnimatePresence>
        {showToast && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="
              fixed top-1/2 left-1/8
              -translate-x-1/2 -translate-y-1/2
              z-[9999]
            "
          >
            <div className="
              flex items-start gap-4
              bg-white/90 backdrop-blur-xl
              border border-green-200
              shadow-2xl
              rounded-2xl
              px-6 py-4
              min-w-[320px]
              max-w-[90vw]
            ">
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="text-green-600" size={22} />
              </div>

              <div>
                <p className="font-semibold text-gray-900">
                  Login successful 🎉
                </p>
                <p className="text-sm text-gray-600 mt-0.5">
                  Welcome back! You are now logged in.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md bg-white/20 backdrop-blur-[25px] border border-white/40 rounded-[2rem] p-10 md:p-14 shadow-2xl"
      >
        <h2 className="text-3xl font-bold text-rose-900 text-center mb-8 tracking-widest uppercase">
          Login
        </h2>

        {/* Login Mode Toggle */}
        <div className="flex bg-white/30 rounded-xl p-1 mb-8">
          <button
            type="button"
            onClick={() => { setMode('email'); setOtpSent(false); }}
            className={`flex-1 py-2 rounded-lg font-bold text-sm transition ${
              mode === 'email'
                ? 'bg-white text-rose-900 shadow'
                : 'text-rose-900/60'
            }`}
          >
            Email Login
          </button>
          <button
            type="button"
            onClick={() => setMode('phone')}
            className={`flex-1 py-2 rounded-lg font-bold text-sm transition ${
              mode === 'phone'
                ? 'bg-white text-rose-900 shadow'
                : 'text-rose-900/60'
            }`}
          >
            OTP Login
          </button>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          {/* EMAIL + PASSWORD */}
          {mode === 'email' && (
            <>
              <div className="relative border-b-2 border-rose-900/20 focus-within:border-rose-500 py-2">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email Address"
                  className="w-full bg-transparent outline-none text-rose-950 placeholder:text-rose-900/40 py-1"
                />
                <Mail className="absolute right-0 top-2 text-rose-900/60" size={20} />
              </div>

              <div className="relative border-b-2 border-rose-900/20 focus-within:border-rose-500 py-2">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Password"
                  className="w-full bg-transparent outline-none text-rose-950 placeholder:text-rose-900/40 py-1"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-2 text-rose-900/60"
                >
                  {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                </button>
              </div>

              <div className="text-right">
                <button
                  type="button"
                  className="text-xs font-bold text-rose-900/60 hover:text-rose-700"
                >
                  Forgot <span className="text-rose-900">Password?</span>
                </button>
              </div>
            </>
          )}

          {/* PHONE + OTP */}
          {mode === 'phone' && (
            <>
              <div className="relative border-b-2 border-rose-900/20 focus-within:border-rose-500 py-2">
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Mobile Number"
                  className="w-full bg-transparent outline-none text-rose-950 placeholder:text-rose-900/40 py-1"
                />
                <Smartphone className="absolute right-0 top-2 text-rose-900/60" size={20} />
              </div>

              {otpSent && (
                <div className="relative border-b-2 border-rose-900/20 focus-within:border-rose-500 py-2">
                  <input
                    type="text"
                    name="otp"
                    value={formData.otp}
                    onChange={handleChange}
                    placeholder="Enter OTP"
                    className="w-full bg-transparent outline-none text-rose-950 placeholder:text-rose-900/40 py-1"
                  />
                  <ShieldCheck className="absolute right-0 top-2 text-rose-900/60" size={20} />
                </div>
              )}

              {!otpSent && (
                <motion.button
                  type="button"
                  whileTap={{ scale: 0.97 }}
                  onClick={sendOtp}
                  className="w-full bg-white/40 text-rose-900 font-bold py-3 rounded-xl"
                >
                  Send OTP
                </motion.button>
              )}
            </>
          )}

          {/* LOGIN BUTTON */}
          {(mode === 'email' || otpSent) && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="w-full bg-gradient-to-r from-rose-400 to-rose-600 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-rose-500/30"
            >
              Login
            </motion.button>
          )}
        </form>

        <div className="mt-8 text-center text-sm font-semibold text-rose-950">
          Don&apos;t have an Account?
          <Link to="/signup" className="ml-1 border-b-2 border-rose-900 font-bold">
            Register
          </Link>
        </div>
      </motion.div>
    </AuthLayout>
  );
};

export default Login;
